import { DEFAULT_LOCALE, type SupportedLocale } from "./config";

interface ScheduleLabelCopy {
    baseline: string;
    day: string;
    week: string;
    month: string;
    year: string;
    hour: string;
    hours: string;
}

const scheduleLabelCopy: Record<SupportedLocale, ScheduleLabelCopy> = {
    "en-GB": {
        baseline: "Baseline",
        day: "Day",
        week: "Week",
        month: "Month",
        year: "Year",
        hour: "hour",
        hours: "hours",
    },
    "en-US": {
        baseline: "Baseline",
        day: "Day",
        week: "Week",
        month: "Month",
        year: "Year",
        hour: "hour",
        hours: "hours",
    },
    "de-DE": {
        baseline: "Ausgangswert",
        day: "Tag",
        week: "Woche",
        month: "Monat",
        year: "Jahr",
        hour: "Stunde",
        hours: "Stunden",
    },
    "da-DK": {
        baseline: "Udgangspunkt",
        day: "Dag",
        week: "Uge",
        month: "Måned",
        year: "År",
        hour: "time",
        hours: "timer",
    },
    "fr-FR": {
        baseline: "Valeur initiale",
        day: "Jour",
        week: "Semaine",
        month: "Mois",
        year: "Année",
        hour: "heure",
        hours: "heures",
    },
    "it-IT": {
        baseline: "Valore iniziale",
        day: "Giorno",
        week: "Settimana",
        month: "Mese",
        year: "Anno",
        hour: "ora",
        hours: "ore",
    },
    "es-ES": {
        baseline: "Valor inicial",
        day: "Día",
        week: "Semana",
        month: "Mes",
        year: "Año",
        hour: "hora",
        hours: "horas",
    },
};

function copyForLocale(locale: SupportedLocale = DEFAULT_LOCALE): ScheduleLabelCopy {
    return scheduleLabelCopy[locale] ?? scheduleLabelCopy[DEFAULT_LOCALE];
}

export function formatAssessmentLabel(
    indexOrNumber: number | string,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    if (locale === "de-DE") return `Bewertung ${indexOrNumber}`;
    if (locale === "da-DK") return `Vurdering ${indexOrNumber}`;
    if (locale === "fr-FR") return `Évaluation ${indexOrNumber}`;
    if (locale === "it-IT") return `Valutazione ${indexOrNumber}`;
    if (locale === "es-ES") return `Evaluación ${indexOrNumber}`;
    return `Assessment #${indexOrNumber}`;
}

function localizeHourCount(count: number, locale: SupportedLocale): string {
    const copy = copyForLocale(locale);
    if (locale === "de-DE") {
        return count === 1 ? `1 ${copy.hour}` : `${count} ${copy.hours}`;
    }
    return count === 1 ? `1 ${copy.hour}` : `${count} ${copy.hours}`;
}

function localizeGermanProcedurePhrases(label: string): string {
    let result = label;

    result = result.replace(/\b(\d+)\s+months?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 Monat vor der Injektion" : `${n} Monate vor der Injektion`;
    });
    result = result.replace(/\b(\d+)\s+weeks?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 Woche vor der Injektion" : `${n} Wochen vor der Injektion`;
    });
    result = result.replace(/\b(\d+)\s+days?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 Tag vor der Injektion" : `${n} Tage vor der Injektion`;
    });
    result = result.replace(/\bBefore Injection\b/gi, "Vor der Injektion");
    result = result.replace(/\bAfter Injection\b/gi, "Nach der Injektion");
    result = result.replace(/\b(\d+)\s+hours?\b/gi, (_, count: string) => localizeHourCount(Number(count), "de-DE"));

    return result;
}

function localizeDanishProcedurePhrases(label: string): string {
    let result = label;

    result = result.replace(/\b(\d+)\s+months?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 måned før injektion" : `${n} måneder før injektion`;
    });
    result = result.replace(/\b(\d+)\s+weeks?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 uge før injektion" : `${n} uger før injektion`;
    });
    result = result.replace(/\b(\d+)\s+days?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 dag før injektion" : `${n} dage før injektion`;
    });
    result = result.replace(/\bBefore Injection\b/gi, "Før injektion");
    result = result.replace(/\bAfter Injection\b/gi, "Efter injektion");
    result = result.replace(/\b(\d+)\s+hours?\b/gi, (_, count: string) => localizeHourCount(Number(count), "da-DK"));

    return result;
}

function localizeFrenchProcedurePhrases(label: string): string {
    let result = label;

    result = result.replace(/\b(\d+)\s+months?\s+before\s+injection\b/gi, (_, count: string) => {
        return `${Number(count)} mois avant l'injection`;
    });
    result = result.replace(/\b(\d+)\s+weeks?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 semaine avant l'injection" : `${n} semaines avant l'injection`;
    });
    result = result.replace(/\b(\d+)\s+days?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 jour avant l'injection" : `${n} jours avant l'injection`;
    });
    result = result.replace(/\bBefore Injection\b/gi, "Avant l'injection");
    result = result.replace(/\bAfter Injection\b/gi, "Après l'injection");
    result = result.replace(/\b(\d+)\s+hours?\b/gi, (_, count: string) => localizeHourCount(Number(count), "fr-FR"));

    return result;
}

function localizeSpanishProcedurePhrases(label: string): string {
    let result = label;

    result = result.replace(/\b(\d+)\s+months?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 mes antes de la inyección" : `${n} meses antes de la inyección`;
    });
    result = result.replace(/\b(\d+)\s+weeks?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 semana antes de la inyección" : `${n} semanas antes de la inyección`;
    });
    result = result.replace(/\b(\d+)\s+days?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 día antes de la inyección" : `${n} días antes de la inyección`;
    });
    result = result.replace(/\bBefore Injection\b/gi, "Antes de la inyección");
    result = result.replace(/\bAfter Injection\b/gi, "Después de la inyección");
    result = result.replace(/\b(\d+)\s+hours?\b/gi, (_, count: string) => localizeHourCount(Number(count), "es-ES"));

    return result;
}

function localizeItalianProcedurePhrases(label: string): string {
    let result = label;

    result = result.replace(/\b(\d+)\s+months?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 mese prima dell'iniezione" : `${n} mesi prima dell'iniezione`;
    });
    result = result.replace(/\b(\d+)\s+weeks?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 settimana prima dell'iniezione" : `${n} settimane prima dell'iniezione`;
    });
    result = result.replace(/\b(\d+)\s+days?\s+before\s+injection\b/gi, (_, count: string) => {
        const n = Number(count);
        return n === 1 ? "1 giorno prima dell'iniezione" : `${n} giorni prima dell'iniezione`;
    });
    result = result.replace(/\bBefore Injection\b/gi, "Prima dell'iniezione");
    result = result.replace(/\bAfter Injection\b/gi, "Dopo l'iniezione");
    result = result.replace(/\b(\d+)\s+hours?\b/gi, (_, count: string) => localizeHourCount(Number(count), "it-IT"));

    return result;
}

/** Fields available on entries, due slots, and combined timeline rows. */
export interface ScheduleStepLabelSource {
    label?: string | null;
    timing_label?: string | null;
    /** Stored on saved entries (`timing_interval`); due slots use `interval`. */
    timing_interval?: number | string | null;
    interval?: number | null;
    time_unit?: string | null;
    schedule_index?: number | null;
}

export interface ResolveScheduleStepLabelOptions {
    /** When nothing else resolves (default: null). */
    fallback?: string | null;
    locale?: SupportedLocale;
}

/** Derive Week/Month/Baseline-style labels from interval + unit (autoname timings). */
export function deriveLabelFromTimingFields(
    timingInterval: number | string | null | undefined,
    timeUnit: string | null | undefined,
    scheduleIndex: number | null | undefined,
    locale: SupportedLocale = DEFAULT_LOCALE
): string | null {
    const copy = copyForLocale(locale);
    if (timingInterval == null || timingInterval === "") {
        return scheduleIndex === 0 ? copy.baseline : null;
    }

    const interval = Number(timingInterval);
    if (Number.isNaN(interval)) {
        return null;
    }

    if (interval === 0) {
        return copy.baseline;
    }

    const unit = timeUnit?.toLowerCase();
    if (unit === "week") return `${copy.week} ${interval}`;
    if (unit === "day") return `${copy.day} ${interval}`;
    if (unit === "month") return `${copy.month} ${interval}`;
    if (unit === "year") return `${copy.year} ${interval}`;
    if (unit === "hour") return localizeHourCount(interval, locale);

    if (scheduleIndex != null) {
        return formatAssessmentLabel(scheduleIndex + 1, locale);
    }

    return null;
}

/**
 * Resolve a schedule step label from stored text and/or timing fields.
 * Prefer `timing_label`, then `label`, then derived Week/Month/etc., then assessment index.
 */
export function resolveScheduleStepLabel(
    source: ScheduleStepLabelSource,
    options?: ResolveScheduleStepLabelOptions
): string | null {
    const locale = options?.locale ?? DEFAULT_LOCALE;
    const explicit = source.timing_label?.trim() || source.label?.trim();
    if (explicit) {
        return localizeStoredScheduleLabel(explicit, locale);
    }

    const interval = source.timing_interval ?? source.interval;
    const fromFields = deriveLabelFromTimingFields(interval, source.time_unit, source.schedule_index, locale);
    if (fromFields) {
        return fromFields;
    }

    if (source.schedule_index != null) {
        return formatAssessmentLabel(source.schedule_index + 1, locale);
    }

    if (options?.fallback !== undefined) {
        return options.fallback;
    }

    return null;
}

/** Display label for UI lists and cards; never returns empty when `schedule_index` is set. */
export function scheduleStepDisplayLabel(
    source: ScheduleStepLabelSource,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    return (
        resolveScheduleStepLabel(source, { locale }) ?? formatAssessmentLabel((source.schedule_index ?? 0) + 1, locale)
    );
}

/** Localise a stored English schedule label such as "Week 4", "Before Injection", or "6 hours". */
export function localizeStoredScheduleLabel(label: string, locale: SupportedLocale = DEFAULT_LOCALE): string {
    const copy = copyForLocale(locale);
    const trimmed = label.trim();
    if (!trimmed) return trimmed;

    if (/^baseline$/i.test(trimmed)) {
        return copy.baseline;
    }

    const intervalMatch = /^(day|week|month|year)\s+(\d+)$/i.exec(trimmed);
    if (intervalMatch) {
        const [, unit, interval] = intervalMatch;
        const unitLabel =
            unit.toLowerCase() === "day"
                ? copy.day
                : unit.toLowerCase() === "week"
                  ? copy.week
                  : unit.toLowerCase() === "month"
                    ? copy.month
                    : copy.year;
        return `${unitLabel} ${interval}`;
    }

    const hoursMatch = /^(\d+)\s+hours?$/i.exec(trimmed);
    if (hoursMatch) {
        return localizeHourCount(Number(hoursMatch[1]), locale);
    }

    const assessmentMatch = /^assessment\s+#?(\d+)$/i.exec(trimmed);
    if (assessmentMatch) {
        return formatAssessmentLabel(assessmentMatch[1], locale);
    }

    if (locale === "de-DE") {
        const localized = localizeGermanProcedurePhrases(trimmed);
        if (localized !== trimmed) {
            return localized;
        }
    }

    if (locale === "da-DK") {
        const localized = localizeDanishProcedurePhrases(trimmed);
        if (localized !== trimmed) {
            return localized;
        }
    }

    if (locale === "fr-FR") {
        const localized = localizeFrenchProcedurePhrases(trimmed);
        if (localized !== trimmed) {
            return localized;
        }
    }

    if (locale === "it-IT") {
        const localized = localizeItalianProcedurePhrases(trimmed);
        if (localized !== trimmed) {
            return localized;
        }
    }

    if (locale === "es-ES") {
        const localized = localizeSpanishProcedurePhrases(trimmed);
        if (localized !== trimmed) {
            return localized;
        }
    }

    return trimmed;
}

function localizeCompactScheduleToken(token: string, locale: SupportedLocale): string {
    const match = /^(\d+)(s|min|h|d|w|m|y)$/i.exec(token.trim());
    if (
        !match ||
        (locale !== "de-DE" && locale !== "fr-FR" && locale !== "da-DK" && locale !== "it-IT" && locale !== "es-ES")
    ) {
        return token;
    }

    const count = Number(match[1]);
    const unit = match[2].toLowerCase();
    if (locale === "da-DK") {
        switch (unit) {
            case "h":
                return `${count} t.`;
            case "d":
                return count === 1 ? "1 dag" : `${count} dage`;
            case "w":
                return count === 1 ? "1 uge" : `${count} uger`;
            case "m":
                return `${count} md.`;
            case "y":
                return `${count} år`;
            case "min":
                return `${count} min`;
            case "s":
                return `${count} s`;
            default:
                return token;
        }
    }
    if (locale === "fr-FR") {
        switch (unit) {
            case "h":
                return `${count} h`;
            case "d":
                return `${count} j`;
            case "w":
                return `${count} sem.`;
            case "m":
                return `${count} mois`;
            case "y":
                return count === 1 ? "1 an" : `${count} ans`;
            case "min":
                return `${count} min`;
            case "s":
                return `${count} s`;
            default:
                return token;
        }
    }
    if (locale === "it-IT") {
        switch (unit) {
            case "h":
                return `${count} h`;
            case "d":
                return `${count} g`;
            case "w":
                return `${count} sett.`;
            case "m":
                return `${count} mesi`;
            case "y":
                return count === 1 ? "1 anno" : `${count} anni`;
            case "min":
                return `${count} min`;
            case "s":
                return `${count} s`;
            default:
                return token;
        }
    }
    if (locale === "es-ES") {
        switch (unit) {
            case "h":
                return `${count} h`;
            case "d":
                return `${count} d`;
            case "w":
                return `${count} sem.`;
            case "m":
                return count === 1 ? "1 mes" : `${count} meses`;
            case "y":
                return count === 1 ? "1 año" : `${count} años`;
            case "min":
                return `${count} min`;
            case "s":
                return `${count} s`;
            default:
                return token;
        }
    }
    switch (unit) {
        case "h":
            return count === 1 ? "1 Std." : `${count} Std.`;
        case "d":
            return count === 1 ? "1 Tag" : `${count} Tage`;
        case "w":
            return count === 1 ? "1 Wo." : `${count} Wo.`;
        case "m":
            return count === 1 ? "1 Mon." : `${count} Mon.`;
        case "y":
            return count === 1 ? "1 J." : `${count} J.`;
        case "min":
            return `${count} Min.`;
        case "s":
            return `${count} s`;
        default:
            return token;
    }
}

/** Localise a comma-separated schedule preview from `formatScheduleSummary`. */
export function localizeSchedulePreviewSummary(
    summary: string | null | undefined,
    locale: SupportedLocale = DEFAULT_LOCALE
): string {
    if (!summary?.trim()) {
        return summary ?? "";
    }

    return summary
        .split(", ")
        .map((part) => {
            const trimmed = part.trim();
            if (trimmed === "…") {
                return "…";
            }
            const localized = localizeStoredScheduleLabel(trimmed, locale);
            if (localized !== trimmed) {
                return localized;
            }
            return localizeCompactScheduleToken(trimmed, locale);
        })
        .join(", ");
}
